import React, { useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import InputMC from './InputMC';
import ModalMC from './ModalMC';
import DatePickerMC from './DatePickerMC';
import TitleMC from './TitleMC';
import IconMC from './IconMC';
import ButtonMC from './ButtonMC';
import CalendarEventReminderSelector, {
  buildRemindersAttributes
} from '../../components/shared/CalendarEventReminderSelector';
import { Z_INDEX } from '../../constants/zIndex';
import { getBrowserTimezone } from '../modules/CalendarEvents/calendarTimezoneUtils';
import './calendarEventModal.css';

const TITLE_MAX_LENGTH = 100;

const parseDate = (value) => {
  if (!value) return new Date();
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? new Date() : parsed;
};

const getInitialSelectedOffsets = (event) => (
  (event?.reminders || [])
    .map((reminder) => reminder.offset_minutes)
    .filter((offsetMinutes, index, offsets) => offsets.indexOf(offsetMinutes) === index)
    .sort((a, b) => a - b)
);

const CalendarEventModalMC = ({
  isOpen,
  event = null,
  defaultStart = null,
  defaultEnd = null,
  onSave,
  onDelete,
  onClose
}) => {
  const { t } = useTranslation('CalendarEventsScreenMC');
  const [submitting, setSubmitting] = useState(false);
  const [selectedOffsets, setSelectedOffsets] = useState([]);
  const isEdit = Boolean(event);

  const initialValues = useMemo(() => {
    const startDate = parseDate(event?.start || event?.starts_at || defaultStart);
    const endDate = parseDate(event?.end || event?.ends_at || defaultEnd || startDate);

    return {
      title: (event?.title || '').slice(0, TITLE_MAX_LENGTH),
      startsAt: startDate,
      endsAt: endDate
    };
  }, [event, defaultStart, defaultEnd]);

  const methods = useForm({
    defaultValues: initialValues
  });

  const {
    handleSubmit,
    reset,
    watch,
    setError,
    clearErrors
  } = methods;

  useEffect(() => {
    reset(initialValues);
    setSelectedOffsets(getInitialSelectedOffsets(event));
  }, [initialValues, reset, event]);

  const startsAt = watch('startsAt');
  const endsAt = watch('endsAt');

  const handleFormSubmit = async (values) => {
    if (!onSave) return;
    const trimmedTitle = values.title?.trim() || '';

    if (!trimmedTitle) {
      setError('title', { type: 'manual', message: t('fields.title_required') });
      return;
    }

    if (trimmedTitle.length > TITLE_MAX_LENGTH) {
      setError('title', { type: 'manual', message: t('fields.title_max_length') });
      return;
    }

    if (values.startsAt && values.endsAt && values.startsAt >= values.endsAt) {
      setError('endsAt', { type: 'manual', message: t('fields.end_before_start') });
      return;
    }

    clearErrors('title');
    clearErrors('endsAt');
    setSubmitting(true);
    try {
      await onSave({
        title: trimmedTitle,
        starts_at: values.startsAt?.toISOString(),
        ends_at: values.endsAt?.toISOString(),
        timezone: getBrowserTimezone(),
        reminders_attributes: buildRemindersAttributes({
          selectedOffsets,
          existingReminders: event?.reminders || []
        })
      });
      onClose?.();
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!onDelete || !event) return;

    setSubmitting(true);
    try {
      await onDelete(event.id);
      onClose?.();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <FormProvider {...methods}>
      <ModalMC
        visible={isOpen}
        onClose={onClose}
        hideCloseButton
        zIndex={Z_INDEX.calendarModal}
        containerClassName="tw-w-[95vw] tw-max-w-[600px]"
        className="!tw-p-0"
        customHeader={
          <div className="tw-flex tw-items-center tw-justify-between tw-px-6 tw-pt-5 tw-pb-4 tw-border-b tw-border-gray-100">
            <TitleMC
              text={isEdit ? t('modal.edit_title') : t('modal.create_title')}
              textClassName="tw-text-lg tw-font-semibold tw-text-gray-900"
            />
            <button
              type="button"
              onClick={onClose}
              className="tw-p-1 tw-text-gray-400 hover:tw-text-gray-600"
              aria-label={t('actions.cancel')}
              disabled={submitting}
            >
              <IconMC name="close-outline" />
            </button>
          </div>
        }
      >
        <form
          className="tw-flex tw-flex-col"
          onSubmit={handleSubmit(handleFormSubmit)}
        >
          <div className="tw-px-6 tw-py-6 tw-flex tw-flex-col tw-gap-4">
            <InputMC
              name="title"
              label={t('fields.title')}
              useFloatingLabel
              required
              maxLength={TITLE_MAX_LENGTH}
            />

            <DatePickerMC
              name="startsAt"
              label={t('fields.starts_at')}
              showTimeInput
              dateFormat="Pp"
              timeInputLabel={t('fields.time')}
              maxDate={endsAt}
              portalId="calendar-event-datepicker-portal"
              popperClassName="calendar-event-datepicker-popper"
              popperPlacement="bottom-start"
            />

            <DatePickerMC
              name="endsAt"
              label={t('fields.ends_at')}
              showTimeInput
              dateFormat="Pp"
              timeInputLabel={t('fields.time')}
              minDate={startsAt}
              portalId="calendar-event-datepicker-portal"
              popperClassName="calendar-event-datepicker-popper"
              popperPlacement="bottom-start"
            />

            <CalendarEventReminderSelector
              selectedOffsets={selectedOffsets}
              existingReminders={event?.reminders || []}
              onChange={setSelectedOffsets}
              label={t('fields.remember')}
              description={t('fields.remember_description')}
              emptyLabel={t('quick_view.no_remember')}
            />
          </div>

          <div className="tw-flex tw-items-center tw-justify-end tw-gap-3 tw-px-6 tw-py-4 tw-border-t tw-border-gray-100">
            <ButtonMC
              text={t('actions.cancel')}
              variant="GRAY"
              onClick={onClose}
              disabled={submitting}
              size="medium"
            />
            {isEdit && (
              <ButtonMC
                text={t('actions.delete')}
                variant="DANGER"
                outlined
                onClick={handleDelete}
                disabled={submitting}
                size="medium"
              />
            )}
            <ButtonMC
              text={isEdit ? t('actions.save') : t('actions.create')}
              variant="PRIMARY"
              type="submit"
              disabled={submitting}
              size="medium"
            />
          </div>
        </form>
      </ModalMC>
    </FormProvider>
  );
};

export default CalendarEventModalMC;
